import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { resetPasswordSchema, type ResetPasswordInput } from "../schemas/login";
import { apiCall, ApiError } from "../lib/apiClient";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

/**
 * `/reset-password/$token` — token-driven password reset.
 *
 * Token = ConsumedToken (1-time-use, R-039) z magic linku v email.
 * Backend validuje 200 OK + auto-consume; replay attempt → 410 EXPIRED_TOKEN.
 */
export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/reset-password/$token",
  component: ResetPasswordComponent,
});

function ResetPasswordComponent() {
  const { token } = Route.useParams();
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });
  const mutation = useMutation({
    mutationFn: (input: ResetPasswordInput) =>
      apiCall<void>(`/api/public/auth/reset-password/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ password: input.password }),
      }),
    onSuccess: () => {
      setServerError(null);
      setSuccess(true);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 410 || err.errorCode === "EXPIRED_TOKEN") {
          setServerError("Odkaz vypršel nebo byl již použit. Požádejte o nový.");
          return;
        }
        setServerError(err.message || "Reset hesla se nezdařil.");
        return;
      }
      setServerError("Neznámá chyba sítě.");
    },
  });

  const validateOnChange = form.formState.isSubmitted;

  return (
    <main className="min-h-screen bg-primary px-4 py-12 sm:bg-secondary md:px-8 md:pt-24">
      <div className="flex w-full flex-col gap-6 bg-primary sm:mx-auto sm:max-w-110 sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-primary md:text-display-xs">Nastavit nové heslo</h1>
          <p className="text-md text-tertiary">Zadejte nové heslo (minimálně 14 znaků).</p>
        </div>
        {success ? (
          <>
            <InlineAlert variant="success" title="Heslo bylo změněno">
              Můžete se nyní přihlásit s novým heslem.
            </InlineAlert>
            <Button color="primary" size="lg" href="/login">
              Přejít na přihlášení
            </Button>
          </>
        ) : (
          <>
            {serverError && <InlineAlert variant="error">{serverError}</InlineAlert>}
            <form
              noValidate
              onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
              className="flex flex-col gap-6"
            >
              <Input
                label="Nové heslo"
                type="password"
                autoComplete="new-password"
                size="lg"
                isRequired
                isInvalid={Boolean(form.formState.errors.password)}
                hint={form.formState.errors.password?.message ?? "Minimálně 14 znaků."}
                value={form.watch("password")}
                onChange={(v) => form.setValue("password", v, { shouldValidate: validateOnChange })}
              />
              <Input
                label="Heslo znovu"
                type="password"
                autoComplete="new-password"
                size="lg"
                isRequired
                isInvalid={Boolean(form.formState.errors.confirmPassword)}
                hint={form.formState.errors.confirmPassword?.message ?? undefined}
                value={form.watch("confirmPassword")}
                onChange={(v) => form.setValue("confirmPassword", v, { shouldValidate: validateOnChange })}
              />
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={mutation.isPending}
                isDisabled={mutation.isPending}
              >
                Nastavit heslo
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
